import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@context/shared/AuthContext';
import { Box, CircularProgress } from '@mui/material';
import { isAuthor, isAccreditor, canAccessAccreditation, resolveLandingPath } from '@config/shared/roles';

// The in-app 403 page (see pages/Forbidden). Role guards below send a signed-in
// user here when they open a page they may not see, instead of silently
// redirecting them somewhere else.
const FORBIDDEN_PATH = '/403';

// The client error pages render inside the client layout, behind this guard.
// The role guards must let them through — otherwise an accreditor sent to
// /403 would be redirected to /403 forever.
const ERROR_PATHS = [FORBIDDEN_PATH, '/404'];

const ProtectedRoute = ({ children, requireAuth = true, requireAdmin = false }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();
  const isErrorPage = ERROR_PATHS.includes(location.pathname);

  // Accreditor → confined to the accreditation overview. Even a known URL to
  // another page (admin or client) is forbidden, not just links they'd reach
  // through the UI.
  const accreditorOutOfScope =
    !loading &&
    requireAuth &&
    isAuthenticated &&
    isAccreditor(user?.roleId) &&
    !location.pathname.startsWith('/accreditation') &&
    !isErrorPage;

  // Accreditation (overview + detail pages) → limited to admins, managers,
  // and accreditors. Everyone else is turned away, even with a known URL.
  const accreditationForbidden =
    !loading &&
    requireAuth &&
    isAuthenticated &&
    location.pathname.startsWith('/accreditation') &&
    !canAccessAccreditation(user?.roleId);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Not authenticated → redirect to login, save where they were trying to go
  // (never an error page: signing in to land on a 403/404 makes no sense)
  if (requireAuth && !isAuthenticated) {
    return (
      <Navigate
        to="/auth/login"
        state={isErrorPage ? undefined : { from: location }}
        replace
      />
    );
  }

  if (accreditorOutOfScope) {
    // /home is everyone else's landing page (e.g. a bookmark, or the generic
    // "home" link) — just take the accreditor to theirs. Anything else is a
    // page they're not allowed to see.
    return location.pathname === '/home' ? (
      <Navigate to="/accreditation" replace />
    ) : (
      <Navigate to={FORBIDDEN_PATH} replace />
    );
  }

  if (accreditationForbidden) {
    return <Navigate to={FORBIDDEN_PATH} replace />;
  }

  // Below author level → admin pages are forbidden
  if (requireAdmin && (!user || !isAuthor(user.roleId))) {
    return <Navigate to={FORBIDDEN_PATH} replace />;
  }

  // Authenticated user on auth page → send them where they were going
  if (!requireAuth && isAuthenticated) {
    const from = location.state?.from?.pathname;
    return <Navigate to={resolveLandingPath(user?.roleId, from)} replace />;
  }

  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node,
  requireAuth: PropTypes.bool,
  requireAdmin: PropTypes.bool,
};

export default ProtectedRoute;
